import React from 'react';
import { useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import moment from 'moment';
import { EventAvailable as EventAvailableIcon } from '@mui/icons-material';
import { GroupedVirtuoso } from 'react-virtuoso';
import { Box, Avatar } from '@mui/material';
import { getRescueClaimer, isRescueClaimed } from '../../../../helpers/RescuesHelper';
import { Colors } from '../../../../assets/theme/Colors';
import { StyledSubHeader } from '../../../rescuerDashboard/rescuerDashboardMobile/Rescues/myFrus/MyFrusMobile';
import RescueMobileCard from '../../../Rescue/RescueMobileCard';
import { NoRescueCard } from '../../../rescuerDashboard/rescuerDashboardMobile/Rescues/RescuerRescuesDashboardMobileContainer';
import useActiveSite from '../../../../hooks/useActiveSite';

const ReceiverMobileSchedule = ({ receiverId }) => {
  const site = useActiveSite();
  const sitesRescues = useSelector((state) => state.entities.sites.rescues);
  const rescuesList = sitesRescues.bySiteId[site.id] || [];
  const filteredRescues = rescuesList.filter((rescue) => rescue.receiver_id === receiverId);

  const { availableRescues30DaysGroups, availableRescues, unclaimedRescuesGroups } = filteredRescues.reduce(
    (acc, curr) => {
      const rescueValue = isRescueClaimed(curr) ? 0 : 1;

      const isRescueFromTodayOrAfter = moment(curr.date, 'YYYYMMDD').isSameOrAfter(moment(), 'day');
      if (isRescueFromTodayOrAfter) {
        acc.availableRescues.push(curr);

        const today = moment().startOf('day');
        const daysDiffFromToday = moment(curr.date, 'YYYYMMDD').diff(today, 'days');
        const groupedBy30DaysBucket = Math.floor(daysDiffFromToday / 31);
        const startingDate = moment(today)
          .add(groupedBy30DaysBucket * 31, 'days')
          .format('YYYYMMDD');

        if (acc.availableRescues30DaysGroups[startingDate]) {
          acc.availableRescues30DaysGroups[startingDate]++;
          acc.unclaimedRescuesGroups[startingDate] = acc.unclaimedRescuesGroups[startingDate] + rescueValue;
        } else {
          acc.availableRescues30DaysGroups[startingDate] = 1;
          acc.unclaimedRescuesGroups[startingDate] = rescueValue;
        }
      }

      return acc;
    },
    {
      availableRescues: [],
      availableRescues30DaysGroups: {},
      unclaimedRescuesGroups: {},
    }
  );

  const availableRescuesSorted = orderBy(
    availableRescues,
    (availableRescue) => moment(`${availableRescue.date} ${availableRescue.pickup_begin}`, 'YYYYMMDD HH:mm:ss'),
    ['asc']
  );

  const availableRescuesCount = availableRescuesSorted.length;

  const groupCounts = Object.values(availableRescues30DaysGroups);
  const groupBy = Object.keys(availableRescues30DaysGroups);

  const isInflight = sitesRescues.inflight;

  const renderGroupContent = (index) => {
    const unclaimedRescuesLeft = unclaimedRescuesGroups[groupBy[index]];
    return (
      <div style={{ paddingBottom: 8 }}>
        <StyledSubHeader
          color={unclaimedRescuesLeft === 0 ? Colors.rescues.claimed.color : Colors.rescues.unclaimed.color}
          text={`${moment(groupBy[index], 'YYYYMMDD').format('MMMM Do')} - ${moment(groupBy[index], 'YYYYMMDD')
            .add(30, 'days')
            .format('MMMM Do')}`}
          icon={
            <Avatar
              sx={[
                { borderRadius: '12px', width: 50, height: 50 },
                unclaimedRescuesLeft > 0 && {
                  backgroundColor: Colors.rescues.unclaimed.color,
                  filter: 'brightness(0.9)',
                },
                unclaimedRescuesLeft === 0 && {
                  backgroundColor: Colors.rescues.claimed.color,
                  filter: 'brightness(0.9)',
                },
              ]}
            >
              <EventAvailableIcon />
            </Avatar>
          }
        />
      </div>
    );
  };

  if (isInflight) {
    return <RescueMobileCard isLoading hideExpandPanel hideDonorLink isReceiverView />;
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 1, 0),
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {!isInflight && filteredRescues.length === 0 && <NoRescueCard text="No rescues planned." />}

      <Box display="flex" flex={1} flexDirection="column" data-introid="schedule-rescues-container">
        <GroupedVirtuoso
          style={{ width: '100%', height: '100%' }}
          totalCount={availableRescuesCount}
          groupCounts={groupCounts}
          groupContent={renderGroupContent}
          itemContent={(index) => {
            const rescue = availableRescuesSorted[index];
            const top = index === 0 ? 0 : 1;
            const bottom = index === availableRescuesCount ? 0 : 1;
            const isClaimed = isRescueClaimed(rescue);
            const claimer = getRescueClaimer(rescue);
            return (
              <Box pt={top} pb={bottom}>
                <RescueMobileCard
                  isLoading={isInflight}
                  key={rescue.id}
                  rescue={rescue}
                  isUnclaimed={!isClaimed}
                  claimer={claimer}
                  isCancelled={rescue.cancelled_by_id !== null}
                  hideExpandPanel
                  hideDonorLink
                  isReceiverView
                  my={2}
                />
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default ReceiverMobileSchedule;
